import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDrawer } from '../../App';
import {
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupIcon from '@mui/icons-material/Group';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';


const NavBarAdmin = () => {
  const storedUserName = localStorage.getItem('nombreUsuario');
  const navigate = useNavigate();
  const location = useLocation();
  const { drawerExpanded, setDrawerExpanded } = useDrawer();

  const handleCerrar = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
    window.location.reload();
  };

  const toggleDrawer = () => {
    setDrawerExpanded(!drawerExpanded);
  };

  const getPageTitle = () => {
    const routeTitles = {
      '/admin/Dashpage': 'Dashboard',
      '/admin/config': 'Configuración',
      '/admin/mipedidosemanal': 'Mi Pedido Semanal',
      '/admin/AbmPlatos': 'Gestión de Platos',
      '/admin/CreatePlatos': 'Crear Plato',
      '/admin/EditarPlatos': 'Editar Plato',
      '/admin/tipos-plato': 'Gestionar Tipos',
      '/admin/bebidas': 'Gestión de Bebidas',
      '/admin/vermenusolo': 'Ver Menús',
      '/admin/CargarMenuSemanal': 'Cargar Menú Semanal',
      '/admin/Seleccion': 'Selección',
      '/admin/AbmUsuarios': 'Gestión de Usuarios',
      '/admin/AbmEmpresas': 'Gestión de Empresas',
      '/admin/AbmDirecciones': 'Gestión de Sedes',
      '/admin/AbmSectores': 'Gestión de Sectores',
      '/admin/AbmPedidos': 'Pedidos',
    };

    for (const [path, title] of Object.entries(routeTitles)) {
      if (location.pathname.startsWith(path)) {
        return title;
      }
    }
    return 'Panel Admin';
  };

  const links = [
    { label: 'Dashboard', href: '/admin/Dashpage', icon: <DashboardIcon /> },
    { label: 'Configuración', href: '/admin/config', icon: <SettingsIcon /> },
    { label: 'Mi Pedido Semanal', href: '/admin/mipedidosemanal', icon: <ListAltIcon /> },
    { label: 'Platos', href: '/admin/AbmPlatos', icon: <RestaurantMenuIcon /> },
    { label: 'Bebidas', href: '/admin/bebidas', icon: <LocalBarIcon /> },
    { label: 'Ver Menús', href: '/admin/vermenusolo', icon: <VisibilityIcon /> },
    { label: 'Cargar Menú', href: '/admin/CargarMenuSemanal', icon: <CloudUploadIcon /> },
    { label: 'Selección', href: '/admin/Seleccion', icon: <AssignmentIcon /> },
    { label: 'Empresas', href: '/admin/AbmEmpresas', icon: <BusinessIcon /> },
    { label: 'Usuarios', href: '/admin/AbmUsuarios', icon: <GroupIcon /> },
    { label: 'Sedes', href: '/admin/AbmDirecciones', icon: <LocationCityIcon /> },
    { label: 'Sectores', href: '/admin/AbmSectores', icon: <ApartmentIcon /> },
    { label: 'Pedidos', href: '/admin/AbmPedidos', icon: <ReceiptIcon /> },
  ];

  return (
    <>
      {/* Permanent Sidebar Drawer */}
      <Drawer
        variant="permanent"
        open={drawerExpanded}
        sx={{
          width: drawerExpanded ? 280 : 80,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerExpanded ? 280 : 80,
            boxSizing: 'border-box',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            transition: 'width 0.3s ease-in-out',
            overflowX: 'hidden',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.2)',
            border: 'none',
          },
        }}
      >
        <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header con Toggle */}
          <Box className="flex items-center justify-between p-4 border-b border-white/20">
            <IconButton
              onClick={toggleDrawer}
              sx={{
                color: 'white',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'scale(1.1)'
                }
              }}
              size="small"
            >
              {drawerExpanded ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
            {drawerExpanded && (
              <span className="text-xs font-semibold text-white/70">Admin Panel</span>
            )}
          </Box>

          {/* Página actual y usuario */}
          {drawerExpanded && (
            <Box className="px-4 py-4 border-b border-white/20">
              <Typography variant="body2" className="text-white/70 text-xs mb-2 uppercase tracking-wider font-semibold">
                Página Actual
              </Typography>
              <Typography variant="body1" className="text-white font-bold mb-4 truncate">
                {getPageTitle()}
              </Typography>

              <Typography variant="body2" className="text-white/70 text-xs mb-2 uppercase tracking-wider font-semibold">
                Usuario
              </Typography>
              <div className="flex items-center gap-2 text-white bg-white/10 p-2 rounded-lg">
                <PersonIcon fontSize="small" />
                <Typography variant="body2" className="font-semibold truncate">
                  {storedUserName}
                </Typography>
              </div>
            </Box>
          )}

          {/* Menu Items */}
          <List sx={{ flexGrow: 1, px: drawerExpanded ? 1 : 0 }}>
            {links.map((link) => (
              <Tooltip
                key={link.href}
                title={drawerExpanded ? '' : link.label}
                placement="right"
                arrow
              >
                <ListItem
                  button
                  onClick={() => navigate(link.href)}
                  sx={{
                    justifyContent: drawerExpanded ? 'initial' : 'center',
                    px: drawerExpanded ? 2 : 1,
                    py: 1.5,
                    my: 0.5,
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateX(4px)',
                    },
                    backgroundColor: location.pathname === link.href ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderLeft: location.pathname === link.href ? '3px solid white' : 'none',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'white',
                      minWidth: drawerExpanded ? 40 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {link.icon}
                  </ListItemIcon>
                  {drawerExpanded && <ListItemText primary={link.label} />}
                </ListItem>
              </Tooltip>
            ))}
          </List>

          {/* Cerrar sesión en el fondo */}
          {drawerExpanded && (
            <Box className="p-3 border-t border-white/20">
              <button
                onClick={handleCerrar}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-300 font-semibold shadow-lg hover:shadow-xl hover:scale-105"
              >
                <CloseIcon fontSize="small" />
                Cerrar sesión
              </button>
            </Box>
          )}

          {/* Tooltip para cerrar sesión cuando está colapsado */}
          {!drawerExpanded && (
            <Tooltip title="Cerrar sesión" placement="right" arrow>
              <Box className="p-2 border-t border-white/20 flex justify-center">
                <IconButton
                  onClick={handleCerrar}
                  sx={{
                    color: '#ff4444',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 68, 68, 0.2)',
                      transform: 'scale(1.1)'
                    }
                  }}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default NavBarAdmin;
